import GridUserInformation from '@zeus/core/grid/GridUserInformation';
import { IReadmeVersion } from '_/common/types/readme.types';
import useOrgAccess from '_/hooks/useOrgAccess';
import { formatRelativeTime } from '_/utils/dateUtils';
import { compareSort } from '_/utils/formatUtils';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Typography } from 'antd';
import Button from 'antd/lib/button';
import Table, { ColumnType } from 'antd/lib/table';
import Tooltip from 'antd/lib/tooltip';
import React, { ReactElement, useMemo } from 'react';

interface IReadmeHistoryProps {
  onPreviewClick: (version: IReadmeVersion) => void;
  onRestoreClick: (version: IReadmeVersion) => void;
  readmeVersions: IReadmeVersion[];
}

const ReadmeHistory = ({ onPreviewClick, onRestoreClick, readmeVersions }: IReadmeHistoryProps): ReactElement => {
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);

  const columnDefs = useMemo<ColumnType<IReadmeVersion>[]>(
    () => [
      {
        dataIndex: 'id',
        key: 'id',
        render: (_, version) => (
          <Button type="default" onClick={() => onPreviewClick(version)}>
            Preview
          </Button>
        ),
        title: 'Preview Version',
      },
      {
        dataIndex: 'published',
        key: 'timestamp',
        sortDirections: ['ascend', 'descend'],
        sorter: compareSort('timestamp'),
        defaultSortOrder: 'descend',
        title: 'Published',
        render: (published: string, version: IReadmeVersion) => (
          <div>
            <div>{published}</div>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              {formatRelativeTime(version.timestamp)}
            </Typography.Text>
          </div>
        ),
      },
      {
        dataIndex: 'editorName',
        key: 'editorName',
        render: (_, version) => <GridUserInformation data={version} field="editor" />,
        sortDirections: ['ascend', 'descend'],
        sorter: compareSort('editorName'),
        title: 'Updated By',
      },
      {
        dataIndex: 'id',
        key: 'id',
        title: 'Actions',
        render: (_, version) =>
          version.isCurrentVersion ? null : (
            <Tooltip title={access ? 'Restore Readme' : 'You do not have the right permissions to restore this readme'}>
              <Button
                className="wb-textTransform-uppercase"
                danger
                disabled={!access}
                onClick={() => onRestoreClick(version)}
                type="primary"
              >
                Restore
              </Button>
            </Tooltip>
          ),
      },
    ],
    [access, onPreviewClick, onRestoreClick],
  );

  return <Table columns={columnDefs} dataSource={readmeVersions} pagination={false} rowKey="id" />;
};

export default ReadmeHistory;
